import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { api, AssetLocation, AssetLocationTypes, KeyManagementTypes, RequestStatus } from "../../../logic/api";
import { SessionContext } from "../../../utility/useSession";
import propTypes from "prop-types";
import TextInputField from "../../editable/EditableField/fields/TextInputField";
import TreeView, { ITreeViewState, TreeViewCommand, TreeViewContext } from "../../treeView/TreeView";
import LocationsTreeViewLoader from "../../treeView/loaders/LocationsTreeViewLoader";
import { LocationsTreeViewDesign } from "../../treeView/designs/LocationsTreeViewDesign";
import BarcodeScanner from "../../thirdParty/react-barcode-reader";
import { ProgressDialog } from "../../notifications/progressDialog/progressDialog";
import ErrorDialog from "../../notifications/errorDialog/ErrorDialog";
import { useTranslation } from "react-i18next";
import { ReleaseFlag, useReleaseManagement } from "../../../utility/feature-management";
import { Location } from "@/pages/booking/BookingMovement/BookingMovement";

const LocationPickerDialog: React.FunctionComponent<{
  title?: string,
  rootId?: number;
  show: boolean;
  onChange: (location: AssetLocation | null) => void,
  selectionTypes: AssetLocationTypes[],
  displayTypes: AssetLocationTypes[],
  arrivalsOnly?: boolean;
  keyManagementTypes?: KeyManagementTypes[],
  keyManagementOverrideTypes?: AssetLocationTypes[],
  subtitle?: string,
  onUnknownMovement?: () => void,
  onCancel?: () => void,
  defaultExpanded?: AssetLocationTypes[],
  displayInactiveLocations?: boolean,
  onCorrection?: () => void,
  locationType?: Location,
  testId?: string,
}> = (props) => {
  const session = useContext(SessionContext);
  const { t } = useTranslation();
  const fetchLocationByCode = api.hooks.assetLocation.getByCode();
  const [err, setErr] = useState<Error | null>(null);
  let rootId = props.rootId;

  const isUnknownMovementReleased = useReleaseManagement(ReleaseFlag.releaseUnknownMovement);
  const isCorrectionMovementReleased = useReleaseManagement(ReleaseFlag.releaseMarkVehicleMovementAsCorrection);

  if (rootId == null) {
    if (session.current.site) {
      if (session.current.site.home) {
        rootId = session.current.site.home.id;
      }
    }
  }

  if (rootId == null) {
    rootId = 0;
  }

  const [filter, setFilter] = useState("");

  const [state, setState] = useState<ITreeViewState>({
    treeRootId: rootId,
    selectedRecord: null,
    flags: {
      types: props.displayTypes,
      filter: filter,
      code: null,
      selectionTypes: props.selectionTypes,
      keyManagementTypes: props.keyManagementTypes,
      arrivalsOnly: props.arrivalsOnly == true,

    },
    defaultExpanded: props.defaultExpanded,
  });

  useEffect(() => {
    if (props.show == true) {
      setErr(null);
      setFilter("");
      setState({
        ...state, selectedRecord: null, flags: {
          types: props.displayTypes,
          filter: "",
          code: null,
          selectionTypes: props.selectionTypes,
          keyManagementTypes: props.keyManagementTypes,
          arrivalsOnly: props.arrivalsOnly == true,
          displayInactiveLocations: props.displayInactiveLocations
        },
      });
    }
  }, [props.show]);


  useEffect(() => {
    setErr(null);

    if (fetchLocationByCode.status === RequestStatus.idle) {
      return;
    }

    const loc = fetchLocationByCode.data;
    if (loc != null && loc.id != null) {
      if (props.selectionTypes.indexOf(loc.type) != -1 || props.selectionTypes.length == 0) {
        props.onChange(loc);
      } else {
        setErr(new Error(t("pickers.locationNotAccepted")));
      }
    } else {
      setErr(new Error(t("pickers.scannedLocNotFound")));
    }
  }, [fetchLocationByCode.data]);

  const submitFilter = () => {

    setState({
      ...state, selectedRecord: null, flags: {
        types: props.displayTypes,
        filter: filter,
        code: null,
        selectionTypes: props.selectionTypes,
        keyManagementTypes: props.keyManagementTypes,
        arrivalsOnly: props.arrivalsOnly == true,
        includeInActive: props.displayInactiveLocations,
      },
    });
  }


  return <Dialog
    open={props.show}
    scroll={'paper'}
    data-testid={props.testId ?? "location-picker-dialog"}>
    {(props.show == true && err == null) && (
      <BarcodeScanner onError={(err: string) => {
        console.log(err);
      }} onScan={(scan: string) => {


        fetchLocationByCode.send(session.current.context, {
          code: scan
        });
      }} minLength={1} />
    )}
    <ErrorDialog err={err} onClose={() => {
      setErr(null);
    }}></ErrorDialog>
    <ProgressDialog status={fetchLocationByCode.status}></ProgressDialog>
    <TreeViewContext.Provider value={{
      state,
      setState: (node) => {
        setState(node);
        if (node.selectedRecord) {
          const selected = node.selectedRecord as AssetLocation;
          const selectionTypeIncluded = props.selectionTypes.indexOf(selected.type) != -1 || props.selectionTypes.length == 0;
          const keyManagementTypeIncluded = (props.keyManagementTypes == null || props.keyManagementTypes.indexOf(selected.keyType) != -1 || props.keyManagementTypes.length == 0);
          const keyManagementTypeContainsAdditional = (props.keyManagementOverrideTypes == null || props.keyManagementOverrideTypes.indexOf(selected.type) != -1 || props.keyManagementOverrideTypes.length == 0);

          if (selectionTypeIncluded) {

            if (keyManagementTypeIncluded || keyManagementTypeContainsAdditional) {
              props.onChange(selected);
            }
          } else {
            setState({
              ...state, command: {
                command: TreeViewCommand.expanded,
                payLoad: node.selectedRecord,
                targetId: node.selectedRecord.id
              }
            });

          }
        }
      },
      treeViewLoader: LocationsTreeViewLoader,
      getNodeDesign: LocationsTreeViewDesign,
    }}>
      <DialogTitle>
        {props.title || t("keys.labels.selectLocation")}
      </DialogTitle>
      {props.subtitle && <DialogContent>{props.subtitle}</DialogContent>}
      <DialogContent style={{ minHeight: 50, overflow: "hidden" }}>
        <TextInputField
          clearOnFocus={true} maxWidth={500}
          label={t("pickers.searchScanBarcode")}
          value={filter} onChange={(val) => {
            setFilter(val);
          }} onSubmit={() => {
            submitFilter();
          }}></TextInputField>
      </DialogContent>
      <DialogContent style={{ minHeight: 300, minWidth: 400 }}>

        <TreeView></TreeView>

      </DialogContent>
      <DialogActions>
        <Button onClick={() => {
          isUnknownMovementReleased && props.onCancel ? props.onCancel() : props.onChange(null);
        }} data-testid="location-picker-cancel-button">{t("general.cancel")}</Button>
        {isCorrectionMovementReleased && props.locationType === Location.VEHICLE && props.onCorrection && <Button onClick={() => {
          if (typeof props.onCorrection === 'function') {
            props.onCorrection();
          }
        }}>{t("booking.labels.correction")}</Button>}
        {isUnknownMovementReleased && props.onUnknownMovement && <Button data-testid="location-picker-unknown-user-button" onClick={() => {
          if (typeof props.onUnknownMovement === 'function') {
            props.onUnknownMovement();
          }
        }}>{t("booking.labels.unknownUser")}</Button>}
      </DialogActions>
    </TreeViewContext.Provider>
  </Dialog>
}

LocationPickerDialog.propTypes = {
  title: propTypes.string,
  rootId: propTypes.number,
  show: propTypes.bool.isRequired,
  displayTypes: propTypes.array.isRequired,
  keyManagementTypes: propTypes.any,
  keyManagementOverrideTypes: propTypes.any,
  selectionTypes: propTypes.array.isRequired,
  onChange: propTypes.func.isRequired,
  arrivalsOnly: propTypes.bool,
  subtitle: propTypes.string,
  onUnknownMovement: propTypes.func,
  onCancel: propTypes.func,
  defaultExpanded: propTypes.array,
  displayInactiveLocations: propTypes.bool,
  onCorrection: propTypes.func,
  locationType: propTypes.any,
}
export default LocationPickerDialog;
